import { UserContext, PageData } from "../Definitions/UserContext";
import { utils } from "../../utils/Utils";
import { GetGlobalPageList } from "./WindowUtils";

/**
 * Constructs the proper URL for navigating to a different location's page.
 * Handles both MySite URLs and custom domain URLs.
 *
 * @param alias - The location alias/nickname
 * @param pageURL - The page path (e.g., "services", "about") - empty string navigates to home
 * @param siteData - UserContext containing vgurl for MySite URL construction
 * @param isLocationNickName - Whether to URL-encode the alias
 * @returns The constructed URL string
 */
export function GetAliasLink(
    alias: string,
    pageURL: string,
    siteData: UserContext,
    isLocationNickName: boolean
): string {
    if (isLocationNickName) {
        alias = encodeURIComponent(alias);
    }

    let url = "";
    const mySiteName = utils.MySiteName();

    if (window.location.origin === mySiteName) {
        // MySite URL format: origin/vgurl/alias/pageURL
        url = `${window.location.origin}/${siteData.vgurl}/${alias}/${pageURL}`;
    } else {
        // Custom domain URL format: origin/alias/pageURL
        url = `${window.location.origin}/${alias}/${pageURL}`;
    }

    // Preserve preview mode if currently in preview
    if (window.location.pathname.endsWith("/preview")) {
        url = `${url}/preview`;
    }

    return url;
}

/**
 * Stores location data in localStorage before navigation.
 * This is required for multi-location navigation - the Consumer app (Pages.jsx)
 * reads this on duplicated pages to determine which business to bind data to.
 *
 * Must be called BEFORE navigation (e.g., in onClick handler before <a> tag default action).
 */
export function SetLocationStorage(
    vagaroURL: string,
    encBID: string,
    bidHash: string
): void {
    const jsonData = JSON.stringify({
        vagaroURL,
        bid: encBID,
        bidHash,
    });
    localStorage.setItem("location", jsonData);
}

/**
 * Resolves a page ID to its URL path.
 * Handles nested pages by flattening the page tree.
 *
 * @param pageID - The page ID to look up
 * @param userContext - UserContext containing pages array
 * @returns The page URL path, or empty string if not found
 */
export function GetPageURLFromPageId(pageID: string, userContext: UserContext): string {
    let pages = userContext.pages;

    if (pages && pages.length > 0) {
        // Flatten nested pages including children
        let newPages: PageData[] = [];
        for (let i = 0; i < pages.length; i++) {
            newPages.push(pages[i]);
            newPages = [...newPages, ...(pages[i].children ?? [])];
        }
        pages = newPages;
    } else {
        pages = GetGlobalPageList();
    }

    let pageURL = "";

    for (let i = 0; i < pages.length; i++) {
        if (pages[i].pageId === pageID) {
            pageURL = pages[i].url;
            break;
        }
    }

    return pageURL;
}
